import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { usersAPI, authAPI } from '../services/api';
import Input from '../components/Input';
import Button from '../components/Button';
import Captcha from '../components/Captcha';
import Modal from '../components/Modal';

const OperatorRegisterPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.from?.pathname || '/ddp/create';
  const [form, setForm] = useState({ companyName: '', address: '', telephone: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [captchaValid, setCaptchaValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const [validationModal, setValidationModal] = useState({ isOpen: false, messages: [] });

  useEffect(() => {
    let cancelled = false;
    authAPI
      .getMaintenanceStatus()
      .then((data) => {
        if (!cancelled) setMaintenanceOn(Boolean(data?.maintenance));
      })
      .catch(() => {
        if (!cancelled) setMaintenanceOn(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requiredFields = ['companyName', 'address', 'telephone', 'email', 'password', 'confirmPassword'];
  const fieldLabels = {
    companyName: t('operator.companyName'),
    address: t('operator.address'),
    telephone: t('operator.telephone'),
    email: t('operator.email'),
    password: t('operator.password'),
    confirmPassword: t('operator.confirmPassword')
  };

  const closeValidationModal = () => {
    setValidationModal({ isOpen: false, messages: [] });
  };

  const openValidationModal = (messages) => {
    const messagesArray = Array.isArray(messages) ? messages : [messages];
    const uniqueMessages = [...new Set(messagesArray.filter(Boolean))];
    setValidationModal({ isOpen: uniqueMessages.length > 0, messages: uniqueMessages });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    // Clear field error when user types
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    if (errors.submit) setErrors(prev => ({ ...prev, submit: undefined }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const messages = [];

    requiredFields.forEach((fieldName) => {
      if (!String(form[fieldName] || '').trim()) {
        nextErrors[fieldName] = t('common.required');
        messages.push(`${fieldLabels[fieldName]}: ${t('common.required')}`);
      }
    });

    if (!captchaValid) {
      messages.push(t('common.captchaRequired'));
    }

    if (form.password && form.confirmPassword && form.password !== form.confirmPassword) {
      nextErrors.confirmPassword = t('operator.passwordMismatch');
      messages.push(t('operator.passwordMismatch'));
    }

    if (messages.length > 0) {
      setErrors(nextErrors);
      openValidationModal(messages);
      return false;
    }

    setErrors({});
    closeValidationModal();
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...payload } = form;
      await usersAPI.registerOperator(payload);
      navigate('/operator/register/pending', {
        replace: true,
        state: { from: { pathname: returnTo } }
      });
    } catch (err) {
      const code = err.response?.data?.code;
      const raw = err.response?.data?.message || err.response?.data?.error || err.message;
      const msg = code === 'MAINTENANCE_MODE' ? t('operator.maintenanceRegisterClosed') : raw;
      setErrors({ submit: msg });
      openValidationModal(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto mt-8 p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">{t('operator.registerTitle')}</h2>
          {maintenanceOn ? (
            <div className="mt-3 flex gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm" role="status">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p>{t('operator.maintenanceRegisterBanner')}</p>
            </div>
          ) : null}
        </div>
        <div className="px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-4">
          <Input label={t('operator.companyName')} name="companyName" value={form.companyName} onChange={handleChange} required error={errors.companyName} />
          <Input label={t('operator.address')} name="address" value={form.address} onChange={handleChange} required error={errors.address} />
          <Input label={t('operator.telephone')} name="telephone" value={form.telephone} onChange={handleChange} required error={errors.telephone} />
          <Input label={t('operator.email')} name="email" type="email" value={form.email} onChange={handleChange} required error={errors.email} autoComplete="off" />
          <Input label={t('operator.password')} name="password" type="password" value={form.password} onChange={handleChange} required minLength={8} error={errors.password} autoComplete="new-password" />
          <Input label={t('operator.confirmPassword')} name="confirmPassword" type="password" value={form.confirmPassword || ''} onChange={handleChange} required error={errors.confirmPassword} autoComplete="new-password" />
              <Captcha onValidate={setCaptchaValid} />
              <Button type="submit" loading={loading} className="w-full sm:w-auto">{t('common.register')}</Button>
            </form>
        </div>
      </div>

      <Modal
        isOpen={validationModal.isOpen}
        onClose={closeValidationModal}
        title={t('clients.statusValidationTitle')}
        size="sm"
        footer={<Button onClick={closeValidationModal}>{t('common.ok') || 'OK'}</Button>}
      >
        <div className="flex gap-3 p-2 rounded-lg border border-red-200 bg-red-50 text-red-800" role="alert" aria-live="polite">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-800">{t('common.errorsPleaseFix')}</p>
            <ul className="mt-1.5 text-sm text-red-700 list-disc list-inside space-y-0.5">
              {validationModal.messages.map((message, index) => (
                <li key={`${message}-${index}`}>{message}</li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OperatorRegisterPage;
